import React, { useState, useEffect } from 'react';

const EMPTY_FORM = {
  company_name: '',
  position_title: '',
  employment_type: 'Full-time',
  start_date: '',
  end_date: '',
  is_current: false,
};

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance', 'Volunteer'];

const AddPositionModal = ({ isOpen, onClose, onSave, position = null }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!(position && position.id);

  useEffect(() => {
    if (!isOpen) return;
    if (isEditing) {
      setFormData({
        company_name: position.company_name || '',
        position_title: position.position_title || '',
        employment_type: position.employment_type || 'Full-time',
        start_date: position.start_date ? position.start_date.split('T')[0] : '',
        end_date: position.end_date ? position.end_date.split('T')[0] : '',
        is_current: !!position.is_current,
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, position]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'is_current' && checked ? { end_date: '' } : {}),
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.company_name.trim()) errs.company_name = 'Company name is required';
    if (!formData.position_title.trim()) errs.position_title = 'Job title is required';
    if (!formData.start_date) errs.start_date = 'Start date is required';
    if (!formData.is_current && !formData.end_date) errs.end_date = 'End date is required (or mark as current)';
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      errs.end_date = 'End date must be after start date';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        end_date: formData.is_current ? null : formData.end_date,
      }, isEditing ? position.id : null);
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to save position. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Position' : 'Add Position'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" disabled={isSubmitting}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{errors.submit}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              placeholder="e.g. Accenture"
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 ${errors.company_name ? 'border-red-300' : 'border-gray-200'}`}
              disabled={isSubmitting}
            />
            {errors.company_name && <p className="mt-1 text-xs text-red-600">{errors.company_name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="position_title"
              value={formData.position_title}
              onChange={handleChange}
              placeholder="e.g. Senior Product Manager"
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 ${errors.position_title ? 'border-red-300' : 'border-gray-200'}`}
              disabled={isSubmitting}
            />
            {errors.position_title && <p className="mt-1 text-xs text-red-600">{errors.position_title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              name="employment_type"
              value={formData.employment_type}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 bg-white"
              disabled={isSubmitting}
            >
              {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 ${errors.start_date ? 'border-red-300' : 'border-gray-200'}`}
                disabled={isSubmitting}
              />
              {errors.start_date && <p className="mt-1 text-xs text-red-600">{errors.start_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                disabled={formData.is_current || isSubmitting}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 disabled:bg-gray-50 disabled:text-gray-400 ${errors.end_date ? 'border-red-300' : 'border-gray-200'}`}
              />
              {errors.end_date && <p className="mt-1 text-xs text-red-600">{errors.end_date}</p>}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              name="is_current"
              checked={formData.is_current}
              onChange={handleChange}
              className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900/20"
              disabled={isSubmitting}
            />
            <span className="text-sm text-gray-700">I currently work here</span>
          </label>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (isEditing ? 'Save changes' : 'Add position')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPositionModal;
